import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {useFormContext} from 'react-hook-form'
import {NotesFormSchema} from '../../formSchemas'
import {z} from 'zod'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {AslFirstAttachment} from '../asl-first-attachment/AslFirstAttachment'
import {AslSecondAttachment} from '../asl-second-attachment/AslSecondAttachment'
import {NotesDescriptionField} from '../notes-description-field/NotesDescriptionField'
import {CardInEditModeType} from '@/pages/project-details-notes/ProjectDetailsNotes'
import {ProjectExtended, ProjectNotesType} from '../../types'
import {useOutletContext} from 'react-router-dom'
import {useStoreProjectNotes} from '../../queries/useStoreProjectNotes'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'

export const AdministrativeActivitiesFormSchema = NotesFormSchema.pick({
    notesDescription: true,
    aslFirstAttachment: true,
    aslSecondAttachment: true
})
export type AdministrativeActivitiesFormSchema = z.infer<typeof AdministrativeActivitiesFormSchema>

interface AdministrativeActivitiesFormProps {
    setCardInEditMode: (cardInEditMode: CardInEditModeType | null) => void
}

export const AdministrativeActivitiesForm = ({setCardInEditMode}: AdministrativeActivitiesFormProps) => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()

    const {
        handleSubmit,
        formState: {isDirty}
    } = useFormContext<AdministrativeActivitiesFormSchema>()
    const queryClient = useQueryClient()

    const {mutate: createProjectNotes, isPending} = useStoreProjectNotes(
        {projectId: project.id},
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_NOTES, project.id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, project.id]})
                setCardInEditMode(null)
            }
        }
    )

    const onSubmit = handleSubmit(data => {
        createProjectNotes({
            note: data.notesDescription ?? null,
            type: ProjectNotesType.enum.administrative_activities,
            firstAttachmentId: data.aslFirstAttachment?.id ?? null,
            secondAttachmentId: data.aslSecondAttachment?.id ?? null
        })
    })

    const handleCancel = () => {
        setCardInEditMode(null)
    }

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:administrative_activity')}
            headingEndSlot={
                <EditDetailsSectionButtons
                    isLoading={isPending}
                    isActive={true}
                    isDisabled={!isDirty}
                    onCancel={handleCancel}
                    onConfirm={onSubmit}
                />
            }
        >
            <Flexbox render="form" direction="column" gap={7} fullWidth>
                <Grid columns={4} gap={4} fullWidth>
                    <AslFirstAttachment />

                    <AslSecondAttachment />
                </Grid>
                <NotesDescriptionField />
            </Flexbox>
        </DetailsFieldsSection>
    )
}
